/**
 * useMaterialCards - 物料卡片 Hook
 *
 * 负责解析各类型物料数据、排序和格式化。
 * 支持根据任务配置的物料 ID 列表进行过滤。
 */

import { computed, unref, type ComputedRef, type Ref } from 'vue-demi';
import type { MaterialSnapshotData } from '@ark/material-checkin-shared';
import {
  getLocalizedValue,
  getTagsFromConfig,
  type LocalizedTag,
  type LanguageCode,
} from '../../shared/types';

/**
 * 不需要关联任务的特殊组件类型
 * 这些组件用于配置而非展示，不需要被任务引用
 */
const SPECIAL_COMPONENT_TYPES = ['milestone_reward_config'];

/**
 * 从 actionFilter (json-logic DSL) 中递归提取物料 ID 列表
 *
 * 支持的 json-logic 格式：
 * - { "in": [{ "var": "targetId" }, ["mat_001", "mat_002"]] }
 * - { "in": [{ "var": "context.m_id" }, ["mat_001"]] }
 * - { "==": [{ "var": "targetId" }, "mat_001"] }
 * - { "and": [...] } 或 { "or": [...] } 嵌套结构
 */
export function extractMaterialIdsFromActionFilter(
  actionFilter: Record<string, unknown> | null | undefined,
): Set<string> {
  const materialIds = new Set<string>();

  if (!actionFilter || typeof actionFilter !== 'object') {
    return materialIds;
  }

  // 递归遍历 json-logic 结构
  function traverse(node: unknown): void {
    if (!node || typeof node !== 'object') return;

    if (Array.isArray(node)) {
      for (const item of node) {
        traverse(item);
      }
      return;
    }

    const obj = node as Record<string, unknown>;

    // 处理 "in" 操作符: { "in": [{ "var": "targetId" }, ["id1", "id2"]] }
    if ('in' in obj && Array.isArray(obj.in) && obj.in.length === 2) {
      const [varPart, arrayPart] = obj.in;

      // 检查是否是 targetId 或 context.material_id 等物料相关字段
      if (typeof varPart === 'object' && varPart !== null && 'var' in varPart) {
        const varName = (varPart as { var: string }).var;
        if (
          varName === 'targetId' ||
          varName === 'context.m_id' ||
          varName === 'context.materialId' ||
          varName === 'context.material_id' ||
          varName === 'context.target_id'
        ) {
          // 提取数组中的物料 ID
          if (Array.isArray(arrayPart)) {
            for (const id of arrayPart) {
              if (typeof id === 'string') {
                materialIds.add(id);
              }
            }
          }
        }
      }
    }

    // 处理 "==" 操作符: { "==": [{ "var": "targetId" }, "id1"] }
    if ('==' in obj && Array.isArray(obj['==']) && obj['=='].length === 2) {
      const [varPart, valuePart] = obj['=='];

      if (typeof varPart === 'object' && varPart !== null && 'var' in varPart) {
        const varName = (varPart as { var: string }).var;
        if (
          varName === 'targetId' ||
          varName === 'context.m_id' ||
          varName === 'context.materialId' ||
          varName === 'context.material_id' ||
          varName === 'context.target_id'
        ) {
          if (typeof valuePart === 'string') {
            materialIds.add(valuePart);
          }
        }
      }
    }

    // 递归处理 "and", "or" 等复合操作符
    for (const key of Object.keys(obj)) {
      if (key === 'and' || key === 'or' || key === 'if') {
        traverse(obj[key]);
      }
    }
  }

  traverse(actionFilter);
  return materialIds;
}

/**
 * 从任务配置列表中提取所有任务共同关联的物料 ID（交集）
 *
 * 设计说明：
 * - 使用交集逻辑，确保展示的物料被所有任务都配置
 * - 这样用户点击任何物料都能同时推进所有任务
 * - 避免出现"点击某物料只推进部分任务"的问题
 * - 重要：如果任何一个任务没有配置物料过滤，则不展示任何物料
 *   （否则用户点击物料时，没配置的任务不会生效）
 */
export function extractAllTaskMaterialIds(
  tasks: Array<{ actionFilter?: Record<string, unknown> | null }>,
): Set<string> {
  // 空任务列表，返回空集合
  if (tasks.length === 0) {
    return new Set<string>();
  }

  // 收集每个任务配置的物料 ID 集合
  const taskMaterialSets: Set<string>[] = [];

  for (const task of tasks) {
    const taskMaterialIds = extractMaterialIdsFromActionFilter(
      task.actionFilter,
    );
    // 重要：如果任何一个任务没有配置物料过滤，则返回空集合
    // 这样可以避免用户点击物料时，某个任务不生效的问题
    if (taskMaterialIds.size === 0) {
      return new Set<string>();
    }
    taskMaterialSets.push(taskMaterialIds);
  }

  // 如果只有一个任务，直接返回该任务的物料
  if (taskMaterialSets.length === 1) {
    return taskMaterialSets[0];
  }

  // 多个任务时，取交集
  // 以第一个任务的物料为基准，过滤出在所有任务中都存在的物料
  const [firstSet, ...restSets] = taskMaterialSets;
  const intersection = new Set<string>();

  Array.from(firstSet).forEach((materialId) => {
    const existsInAllTasks = restSets.every((set) => set.has(materialId));
    if (existsInAllTasks) {
      intersection.add(materialId);
    }
  });

  return intersection;
}

/**
 * 文章卡片数据
 */
export interface ArticleCardData {
  materialId: string;
  title: string;
  coverUrl: string;
  jumpUrl: string;
  updateTime?: string;
}

/**
 * 股票洞察卡片数据
 */
export interface StockInsightCardData {
  materialId: string;
  content: string;
  stockId: string;
  /** 卡片跳转链接（点击卡片时使用，计入打卡） */
  cardUrl: string;
  /** 股票标签跳转链接（点击股票标签时使用，不计入打卡） */
  stockUrl: string;
  updateTime?: string;
  // 以下字段由行情接口填充
  symbol?: string;
  name?: string;
  changePercent?: number;
}

/**
 * 直播 Banner 卡片数据
 */
export interface LiveBannerCardData {
  materialId: string;
  coverUrl: string;
  jumpUrl: string;
  liveTime: string;
  tags: string[];
}

/**
 * 期权课堂卡片数据
 */
export interface OptionClassroomCardData {
  materialId: string;
  title: string;
  coverUrl: string;
  jumpUrl: string;
  tags: string[];
}

/**
 * useMaterialCards Hook 配置
 */
export interface UseMaterialCardsOptions {
  /** 物料数据列表 */
  materials: MaterialSnapshotData[] | Ref<MaterialSnapshotData[]>;
  /** 当前语言 */
  lang: LanguageCode;
  /**
   * 任务配置列表（用于过滤物料）
   * 如果提供，只展示被任务配置引用的物料
   * 里程碑物料配置等特殊组件不需要被任务引用
   */
  tasks?:
    | Array<{ actionFilter?: Record<string, unknown> | null }>
    | Ref<Array<{ actionFilter?: Record<string, unknown> | null }>>;
}

/**
 * useMaterialCards Hook 返回值
 */
export interface UseMaterialCardsReturn {
  /** 文章卡片列表 */
  articleCards: ComputedRef<ArticleCardData[]>;
  /** 股票洞察卡片列表 */
  stockInsightCards: ComputedRef<StockInsightCardData[]>;
  /** 直播 Banner 卡片列表 */
  liveBannerCards: ComputedRef<LiveBannerCardData[]>;
  /** 期权课堂卡片列表 */
  optionClassroomCards: ComputedRef<OptionClassroomCardData[]>;
}

/**
 * 按更新时间排序（新的在前）
 */
export function sortByUpdateTime<T extends { updateTime?: string }>(
  items: T[],
): T[] {
  return [...items].sort((a, b) => {
    if (!a.updateTime && !b.updateTime) return 0;
    if (!a.updateTime) return 1;
    if (!b.updateTime) return -1;
    return new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime();
  });
}

/**
 * 解析文章卡片
 */
export function parseArticleCards(
  materials: MaterialSnapshotData[],
  lang: LanguageCode,
): ArticleCardData[] {
  return materials
    .filter((m) => m.componentType === 'article_card')
    .map((m) => ({
      materialId: m.materialId,
      title: getLocalizedValue(m.displayConfig.title, lang) || '',
      coverUrl: getLocalizedValue(m.displayConfig.coverImage, lang) || '',
      jumpUrl: getLocalizedValue(m.displayConfig.url, lang) || '',
      updateTime: m.displayConfig.updateTime as string | undefined,
    }));
}

/**
 * 解析股票洞察卡片
 */
export function parseStockInsightCards(
  materials: MaterialSnapshotData[],
  lang: LanguageCode,
): StockInsightCardData[] {
  return materials
    .filter((m) => m.componentType === 'stock_insight_card')
    .map((m) => ({
      materialId: m.materialId,
      content: getLocalizedValue(m.displayConfig.description, lang) || '',
      stockId: (m.displayConfig.stockId as string) || '',
      cardUrl: getLocalizedValue(m.displayConfig.url, lang) || '',
      stockUrl: getLocalizedValue(m.displayConfig.stockLink, lang) || '',
      updateTime: m.displayConfig.updateTime as string | undefined,
    }));
}

/**
 * 解析直播 Banner 卡片
 */
export function parseLiveBannerCards(
  materials: MaterialSnapshotData[],
  lang: LanguageCode,
): LiveBannerCardData[] {
  return materials
    .filter((m) => m.componentType === 'live_banner_card')
    .map((m) => ({
      materialId: m.materialId,
      coverUrl: getLocalizedValue(m.displayConfig.coverImage, lang) || '',
      jumpUrl: getLocalizedValue(m.displayConfig.url, lang) || '',
      liveTime: (m.displayConfig.liveTime as string) || '',
      tags: getTagsFromConfig(
        m.displayConfig.tags as LocalizedTag[] | undefined,
        lang,
      ),
    }));
}

/**
 * 解析期权课堂卡片
 */
export function parseOptionClassroomCards(
  materials: MaterialSnapshotData[],
  lang: LanguageCode,
): OptionClassroomCardData[] {
  return materials
    .filter((m) => m.componentType === 'option_classroom_card')
    .map((m) => ({
      materialId: m.materialId,
      title: getLocalizedValue(m.displayConfig.title, lang) || '',
      coverUrl: getLocalizedValue(m.displayConfig.coverImage, lang) || '',
      jumpUrl: getLocalizedValue(m.displayConfig.url, lang) || '',
      tags: getTagsFromConfig(
        m.displayConfig.tags as LocalizedTag[] | undefined,
        lang,
      ),
    }));
}

/**
 * 过滤物料列表，只保留任务配置引用的物料
 * 特殊组件类型（如里程碑配置）不需要被任务引用
 *
 * 注意：如果没有配置任何物料，则不展示任何普通物料（只保留特殊组件）
 */
export function filterMaterialsByTaskConfig(
  materials: MaterialSnapshotData[],
  taskMaterialIds: Set<string>,
): MaterialSnapshotData[] {
  return materials.filter((material) => {
    // 特殊组件类型不需要被任务引用
    if (SPECIAL_COMPONENT_TYPES.includes(material.componentType)) {
      return true;
    }
    // 其他物料必须被任务配置引用
    // 如果没有配置任何物料（taskMaterialIds 为空），则不展示任何普通物料
    return taskMaterialIds.has(material.materialId);
  });
}

/**
 * 物料卡片 Hook
 *
 * @example
 * ```typescript
 * const {
 *   articleCards,
 *   stockInsightCards,
 *   liveBannerCards,
 *   optionClassroomCards,
 * } = useMaterialCards({
 *   materials: pageData.configSnapshot.materials,
 *   tasks: pageData.configSnapshot.tasks,
 *   lang,
 * });
 * ```
 */
export function useMaterialCards(
  options: UseMaterialCardsOptions,
): UseMaterialCardsReturn {
  const { materials, lang, tasks } = options;

  const taskMaterialIds = computed<Set<string>>(() => {
    if (!tasks) return new Set();
    const taskList = unref(tasks) as Array<{
      actionFilter?: Record<string, unknown> | null;
    }>;
    if (!taskList || taskList.length === 0) {
      return new Set();
    }
    return extractAllTaskMaterialIds(taskList);
  });

  const filteredMaterials = computed<MaterialSnapshotData[]>(() => {
    const materialList = unref(materials) as MaterialSnapshotData[];
    if (!materialList || materialList.length === 0) return [];
    return filterMaterialsByTaskConfig(materialList, taskMaterialIds.value);
  });

  /**
   * 文章卡片列表（按更新时间排序）
   */
  const articleCards = computed<ArticleCardData[]>(() => {
    if (filteredMaterials.value.length === 0) return [];
    const cards = parseArticleCards(filteredMaterials.value, lang);
    return sortByUpdateTime(cards);
  });

  /**
   * 股票洞察卡片列表（按更新时间排序）
   */
  const stockInsightCards = computed<StockInsightCardData[]>(() => {
    if (filteredMaterials.value.length === 0) return [];
    const cards = parseStockInsightCards(filteredMaterials.value, lang);
    return sortByUpdateTime(cards);
  });

  /**
   * 直播 Banner 卡片列表
   */
  const liveBannerCards = computed<LiveBannerCardData[]>(() => {
    if (filteredMaterials.value.length === 0) return [];
    return parseLiveBannerCards(filteredMaterials.value, lang);
  });

  /**
   * 期权课堂卡片列表
   */
  const optionClassroomCards = computed<OptionClassroomCardData[]>(() => {
    if (filteredMaterials.value.length === 0) return [];
    return parseOptionClassroomCards(filteredMaterials.value, lang);
  });

  return {
    articleCards,
    stockInsightCards,
    liveBannerCards,
    optionClassroomCards,
  };
}

export default useMaterialCards;
